import { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, Paperclip, Send, X } from 'lucide-react';
import ModalOverlay from './ModalOverlay';
import { authedFetch } from '../lib/authedFetch';

/**
 * "Improve this" — a change request raised from inside the app.
 *
 * The snapshot is taken of the page BEHIND this modal, and it is taken on
 * open rather than on submit: by the time someone has typed their note the
 * thing they were pointing at may have scrolled away, and a screenshot of a
 * dialog explaining a problem is not a screenshot of the problem.
 */

// Vercel caps a serverless body at 4.5 MB whatever the config says, and a
// data URL is ~33% larger than its bytes. Keep well under.
const MAX_ATTACHMENT_BYTES = 3_000_000;
const CAPTURE_SCALE = 0.7;

export default function AppImprovementModal({ open, onClose, pageLabel = '', zIndex = 1400 }) {
  const [comment, setComment] = useState('');
  const [shot, setShot] = useState(null);          // { dataUrl, name, type }
  const [capturing, setCapturing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  // A WebGL canvas is blank in an html2canvas capture unless the context
  // was created with preserveDrawingBuffer, which costs frame rate on
  // every pan of the map. Rather than slow the map down for everyone to
  // make one screenshot work, say so and offer the file picker.
  const [glBlank, setGlBlank] = useState(false);
  const [done, setDone] = useState('');
  const fileRef = useRef(null);

  // Snapshot on open, before the reporter's attention moves.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setComment('');
    setShot(null);
    setError('');
    setDone('');
    setGlBlank(!!document.querySelector('canvas.mapboxgl-canvas, canvas.maplibregl-canvas'));
    setCapturing(true);
    (async () => {
      try {
        const { default: html2canvas } = await import('html2canvas');
        // A frame for the overlay to paint, so it is excluded by the
        // ignoreElements filter below rather than caught mid-fade.
        await new Promise((resolve) => setTimeout(resolve, 60));
        const canvas = await html2canvas(document.body, {
          scale: CAPTURE_SCALE,
          useCORS: true,
          logging: false,
          backgroundColor: '#0b1b33',
          // Leave this dialog out of its own screenshot.
          ignoreElements: (el) => el?.dataset?.appImprovementModal === 'true',
        });
        if (cancelled) return;
        setShot({
          dataUrl: canvas.toDataURL('image/jpeg', 0.72),
          name: 'screenshot.jpg',
          type: 'image/jpeg',
        });
      } catch {
        // A failed capture is not a failed report — the words matter more.
        if (!cancelled) setShot(null);
      } finally {
        if (!cancelled) setCapturing(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const pickFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError('That file is larger than 3 MB. Try a smaller one.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setError('');
      setShot({ dataUrl: String(reader.result || ''), name: file.name, type: file.type });
    };
    reader.onerror = () => setError('That file could not be read.');
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    const text = comment.trim();
    if (!text || sending) return;
    setSending(true);
    setError('');
    try {
      const response = await authedFetch('/api/app-improvement/submit', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          comment: text,
          pageUrl: window.location.href,
          pageLabel,
          attachment: shot ? { name: shot.name, type: shot.type, dataUrl: shot.dataUrl } : null,
        }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json?.error || `Could not send (${response.status})`);
      setDone(json?.warning || 'Sent. You will hear back once it is reviewed.');
    } catch (err) {
      setError(err?.message || 'Could not send that.');
    } finally {
      setSending(false);
    }
  };

  return (
    // Above whatever it is reporting on, and deliberately not counted
    // as an overlay itself — registering would have it chase its own
    // z-index upward on every render.
    <ModalOverlay onClose={onClose} zIndex={zIndex} register={false}>
      <div
        data-app-improvement-modal="true"
        style={{ width: 'min(560px, 94vw)', background: '#ffffff', border: '1px solid #e2e8f0' }}
      >
        <div data-modal-head style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.7rem 0.9rem', background: '#003279', color: '#ffffff' }}>
          <span style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', flex: 1 }}>
            Suggest an improvement
          </span>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'transparent', border: 'none', color: '#cfe3f5', cursor: 'pointer', display: 'flex' }}>
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div style={{ padding: '1.4rem 1rem', display: 'grid', gap: 12, justifyItems: 'center', textAlign: 'center' }}>
            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#065f46' }}>{done}</div>
            <button type="button" onClick={onClose} style={primaryButton(false)}>Close</button>
          </div>
        ) : (
          <div style={{ padding: '0.9rem', display: 'grid', gap: 10 }}>
            <div style={{ fontSize: '0.74rem', color: '#475569', lineHeight: 1.5 }}>
              What should this do differently? A snapshot of the page is attached
              automatically — swap it for a file if something else shows it better.
            </div>

            {/* The map draws through WebGL, which a page capture cannot
                read back. Better to say so than to send a report with a
                blank rectangle where the problem was. */}
            {glBlank && (
              <div
                data-app-improvement-gl-note
                style={{
                  display: 'flex',
                  gap: 8,
                  padding: '0.5rem 0.6rem',
                  background: '#fef3c7',
                  border: '1px solid #d1a04a',
                  borderLeft: '4px solid #b45309',
                  color: '#78350f',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  lineHeight: 1.45,
                }}
              >
                The map itself will be blank in this snapshot — the panels and
                labels come through, but the map draws in a way a page capture
                cannot read. For the map itself, take a screenshot with your
                system shortcut and attach it below.
              </div>
            )}

            <textarea
              data-app-improvement-comment
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              rows={5}
              maxLength={4000}
              placeholder="The commission column should show the coordinator's split, not just the total…"
              style={{
                width: '100%', padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: 0,
                fontSize: '0.82rem', lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit',
              }}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: '#475569' }}>
                {capturing ? <Loader2 size={13} className="spin" /> : <Camera size={13} style={{ color: '#047857' }} />}
                {capturing
                  ? 'Taking a snapshot…'
                  : shot
                    ? `Attached: ${shot.name}`
                    : 'No attachment'}
              </div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                style={{
                  marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '0.35rem 0.55rem', background: '#f8fafc', border: '1px solid #cbd5e1',
                  borderRadius: 0, fontSize: '0.72rem', fontWeight: 600, color: '#0f172a', cursor: 'pointer',
                }}
              >
                <Paperclip size={12} /> Attach a file
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,application/pdf"
                onChange={pickFile}
                style={{ display: 'none' }}
              />
            </div>

            {shot?.type?.startsWith('image/') ? (
              <img
                src={shot.dataUrl}
                alt="Attachment preview"
                style={{ width: '100%', maxHeight: 180, objectFit: 'contain', border: '1px solid #e2e8f0', background: '#f8fafc' }}
              />
            ) : null}

            {error ? (
              <div data-app-improvement-error style={{ fontSize: '0.72rem', color: '#b91c1c', lineHeight: 1.45 }}>{error}</div>
            ) : null}

            <button
              type="button"
              data-app-improvement-send
              disabled={sending || !comment.trim()}
              onClick={submit}
              style={primaryButton(sending || !comment.trim())}
            >
              {sending ? <Loader2 size={13} className="spin" /> : <Send size={13} />}
              {sending ? 'Sending…' : 'Send for review'}
            </button>
          </div>
        )}
      </div>
    </ModalOverlay>
  );
}

function primaryButton(disabled) {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '0.55rem 0.8rem',
    background: disabled ? '#e2e8f0' : '#022363',
    border: `1px solid ${disabled ? '#e2e8f0' : '#022363'}`,
    borderRadius: 0,
    color: disabled ? '#94a3b8' : '#ffffff',
    fontSize: '0.78rem', fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
